import { ShieldAlert, ShieldCheck, ShieldX } from "lucide-react";

/**
 * Compact "X pending review" badge surfaced on project cards (list
 * pages) and the project detail header. Renders nothing when there's
 * nothing to flag, so the dashboard stays clean for fully-reviewed jobs.
 *
 * Props:
 *   summary: `{pending, approved, rejected, total}` — the shape returned
 *            by the backend on the projects list + detail endpoints.
 *   variant: "compact" (small inline pill, used on cards) or "header"
 *            (bigger, more prominent — used on the project detail page).
 *   onClick: optional handler so clicking the badge jumps the user to
 *            the Photos tab where they can act.
 */
export default function ReviewSummaryBadge({ summary, variant = "compact", onClick }) {
  const s = summary || {};
  const pending = s.pending || 0;
  const rejected = s.rejected || 0;
  // Nothing waiting on a PM → don't add visual noise.
  if (pending === 0 && rejected === 0) return null;

  // Rejected wins the visual hierarchy (red) over pending (amber) since
  // it implies the field crew still owes a retake.
  const isRejection = rejected > 0;
  const Icon = isRejection ? ShieldX : ShieldAlert;
  const palette = isRejection
    ? "bg-red-950/70 border-red-700 text-red-200"
    : "bg-amber-950/60 border-amber-700 text-amber-200";

  const sizing = variant === "header"
    ? "text-xs px-3 py-1.5 gap-2"
    : "text-[10px] px-2 py-0.5 gap-1.5";

  const label = isRejection
    ? `${rejected} retake${rejected === 1 ? "" : "s"} pending`
    : `${pending} pending review`;

  const Wrapper = onClick ? "button" : "span";
  return (
    <Wrapper
      type={onClick ? "button" : undefined}
      onClick={onClick}
      className={`inline-flex items-center font-display font-bold uppercase tracking-wider rounded-full border ${palette} ${sizing} ${onClick ? "hover:brightness-110 transition" : ""}`}
      data-testid={`review-summary-badge${isRejection ? "-rejection" : "-pending"}`}
      title={
        // Surface full breakdown on hover so a PM can scan a single
        // tooltip and know what's left to do.
        `${s.pending || 0} pending · ${s.rejected || 0} needs retake · ${s.approved || 0} approved`
      }
    >
      <Icon className={variant === "header" ? "w-4 h-4" : "w-3 h-3"} />
      {label}
    </Wrapper>
  );
}

/**
 * Tiny "X / Y approved" rail — drops in on the project detail header
 * alongside the existing status pills. Always shown when there's at
 * least one photo so the PM sees their decision history at a glance.
 */
export function ReviewSummaryRail({ summary, onClick }) {
  const s = summary || {};
  if ((s.total || 0) === 0) return null;
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-3 text-[11px] font-mono text-slate-400 hover:text-slate-100 border border-slate-800 rounded-md px-2.5 py-1 transition"
      data-testid="review-summary-rail"
      title="Click to open the Photos tab"
    >
      <span className="inline-flex items-center gap-1">
        <ShieldCheck className="w-3 h-3 text-emerald-400" />
        {s.approved || 0}
      </span>
      <span className="inline-flex items-center gap-1">
        <ShieldAlert className="w-3 h-3 text-amber-400" />
        {s.pending || 0}
      </span>
      <span className="inline-flex items-center gap-1">
        <ShieldX className="w-3 h-3 text-red-400" />
        {s.rejected || 0}
      </span>
    </button>
  );
}
